import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';

@Component({
    selector: 'app-pedido-publico',
    standalone: true,
    imports: [LoadingIndicatorComponent, CommonModule],
    templateUrl: './pedido-publico.component.html',
    styleUrl: './pedido-publico.component.css'
})
export class PedidoPublicoComponent implements OnInit {
    carregando = signal(true);
    erro = '';
    pedido: any = null;
    itens: any[] = [];

    private cnpjChave = '';

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sankhyaGateway: SankhyaGatewayService
    ) {}

    async ngOnInit(): Promise<void> {
        const chave = this.route.snapshot.paramMap.get('chave') || '';
        const dados = this.decodificarChave(chave);

        if (!dados) {
            this.erro = 'Link do pedido invalido.';
            this.carregando.set(false);
            return;
        }

        this.cnpjChave = dados.cnpj;

        try {
            const response: any = await this.respostaGateway(
                this.sankhyaGateway.consultarOrcamentoImpressao({ NUNOTA: Number(dados.nunota) })
            );
            const pedido = response?.orcamento || response;
            const cnpjPedido = this.valor(pedido, 'CNPJ').replace(/\D/g, '');

            if (cnpjPedido && cnpjPedido !== this.cnpjChave) {
                this.erro = 'Pedido nao localizado para este link.';
                this.pedido = null;
                this.itens = [];
                return;
            }

            this.pedido = pedido;
            this.itens = Array.isArray(pedido?.items) ? pedido.items : [];
        } catch (e) {
            console.error('Erro ao carregar pedido publico:', e);
            this.erro = 'Nao foi possivel carregar os dados do pedido.';
        } finally {
            this.carregando.set(false);
        }
    }

    numeroVenda(): string {
        const numero = this.valor(this.pedido, 'NUMNOTA').replace(/\D/g, '').padStart(7, '0');
        const data = this.valor(this.pedido, 'DTNEG') || this.valor(this.pedido, 'DTATUAL');
        const ano = data.match(/\d{2}\/\d{2}\/(\d{4})/)?.[1] || '';
        return numero && ano ? `${numero}/${ano.slice(-2)}` : numero || this.valor(this.pedido, 'NUNOTA');
    }

    enderecoEntregaLinha(): string {
        const enderecoCompleto = this.valorPrimeiro(['ENDENTREGA_AD', 'ENDENTREGA']);
        if (enderecoCompleto) return enderecoCompleto.split(' - ')[0] || enderecoCompleto;

        const endereco = this.valorPrimeiro(['NOMEENDENTREGA', 'NOMEEND']);
        const numero = this.valorPrimeiro(['NUMENDENTREGA', 'NUMEND']);
        return [endereco, numero].filter(Boolean).join(' ') || '-';
    }

    cidadeEntrega(): string {
        const cidade = this.valorPrimeiro(['CIDADEENTREGA', 'NOMECIDENTREGA', 'NOMECID']);
        const uf = this.valorPrimeiro(['UFENTREGA', 'UF']);
        return [cidade, uf].filter(Boolean).join('-') || '-';
    }

    itemPartNumber(item: any): string {
        return this.valor(item, 'PARTNUMBER') || this.valor(item, 'REFFORN') || this.valor(item, 'CODPROD') || '-';
    }

    itemDescricao(item: any): string {
        return this.valor(item, 'DESCRPROD') || this.valor(item, 'DESCRICAO') || this.valor(item, 'DESCRICAOPROD') || '-';
    }

    itemCondicao(item: any): string {
        return this.valor(item, 'CONDICAO') || this.valor(item, 'CD') || 'NEW';
    }

    itemQtd(item: any): string {
        return Number(this.valor(item, 'QTDNEG') || this.valor(item, 'QTD') || 0).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    itemFabricante(item: any): string {
        return this.valor(item, 'FABRICANTE') || this.valor(item, 'MARCA') || '-';
    }

    itemSerial(item: any): string {
        return this.valor(item, 'AD_SERIAL') || this.valor(item, 'SERIAL') || this.valor(item, 'SERIE') || this.valor(item, 'CONTROLE') || '';
    }

    valor(data: any, key: string): string {
        return data?.[key] == null ? '' : String(data[key]);
    }

    private valorPrimeiro(keys: string[]): string {
        for (const key of keys) {
            const value = this.valor(this.pedido, key).trim();
            if (value) return value;
        }
        return '';
    }

    private async respostaGateway<T = any>(observable: any): Promise<T> {
        const value: any = await firstValueFrom(observable);
        if (!value) throw new Error('Resposta vazia do gateway.');
        if (value.status && value.status !== '1') throw new Error(value.statusMessage || 'Service nao retornou sucesso.');
        return (value.responseBody || value) as T;
    }

    private decodificarChave(chave: string): { nunota: string; cnpj: string } | null {
        try {
            const normalizada = chave.replace(/-/g, '+').replace(/_/g, '/');
            const padding = '='.repeat((4 - normalizada.length % 4) % 4);
            const bytes = atob(normalizada + padding);
            const texto = new TextDecoder().decode(Uint8Array.from(bytes, (char) => char.charCodeAt(0)));
            const [nunota, cnpj] = texto.split('|');
            const nunotaLimpa = String(nunota || '').replace(/\D/g, '');
            const cnpjLimpo = String(cnpj || '').replace(/\D/g, '');
            return nunotaLimpa && cnpjLimpo ? { nunota: nunotaLimpa, cnpj: cnpjLimpo } : null;
        } catch {
            return null;
        }
    }
}
